import React, { useRef, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown, Check, RotateCcw, Volume2, Image as ImageIcon, Plus, X, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { VIDEO_MODELS } from "@/config/videoModels";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MotionPresetGallery, isMotionPreset } from "@/components/MotionPresetGallery";
export interface VideoSettings {
  model: string;
  duration: number;
  aspect_ratio: string;
  quality: string;
  motion_mode: string;
  style: string;
  effect: string;
  negative_prompt: string;
  start_image: string;
  last_frame_image: string;
  sound_effect_switch: boolean;
  sound_effect_content: string;
  seed?: number;
  enable_audio: boolean;
  motion_video: string;
  motion_video_duration?: number; // Actual duration of uploaded motion video (for Kling Motion Control)
  reference_images: string[]; // For Veo 3.1 multiple reference images
}

interface VideoQuickSettingsProps {
  settings: VideoSettings;
  onSettingsChange: (settings: VideoSettings) => void;
  hasChangedSettings: boolean;
  onUseDefaults: () => void;
}

export function VideoQuickSettings({
  settings,
  onSettingsChange,
  hasChangedSettings,
  onUseDefaults,
}: VideoQuickSettingsProps) {
  const currentModel = VIDEO_MODELS[settings.model] || VIDEO_MODELS['pixverse-v5'];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastFrameInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingLastFrame, setIsUploadingLastFrame] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const { toast } = useToast();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 10MB", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `video-start-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      onSettingsChange({ ...settings, start_image: publicUrl });
      toast({ title: "Image uploaded", description: "Start image set for video" });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: "Upload failed", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    onSettingsChange({ ...settings, start_image: '', last_frame_image: '' });
  };

  const handleLastFrameSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 10MB", variant: "destructive" });
      return;
    }

    setIsUploadingLastFrame(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `video-last-frames/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      onSettingsChange({ ...settings, last_frame_image: publicUrl });
      toast({ title: "Image uploaded", description: "Last frame image set" });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: "Upload failed", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setIsUploadingLastFrame(false);
      if (lastFrameInputRef.current) lastFrameInputRef.current.value = '';
    }
  };

  const handleRemoveLastFrame = () => {
    onSettingsChange({ ...settings, last_frame_image: '' });
  };

  const handleVideoSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast({ title: "Invalid file", description: "Please select MP4, WebM, or MOV video", variant: "destructive" });
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 50MB", variant: "destructive" });
      return;
    }

    // Extract video duration before uploading
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';
    
    const getDuration = new Promise<number>((resolve, reject) => {
      videoElement.onloadedmetadata = () => {
        URL.revokeObjectURL(videoElement.src);
        const durationSeconds = Math.ceil(videoElement.duration);
        resolve(durationSeconds);
      };
      videoElement.onerror = () => {
        URL.revokeObjectURL(videoElement.src);
        reject(new Error('Could not read video metadata'));
      };
      videoElement.src = URL.createObjectURL(file);
    });

    try {
      const videoDuration = await getDuration;
      
      // Validate max 30 seconds
      if (videoDuration > 30) {
        toast({ title: "Video too long", description: "Maximum duration is 30 seconds", variant: "destructive" });
        if (videoInputRef.current) videoInputRef.current.value = '';
        return;
      }

      setIsUploadingVideo(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `motion-references/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('videos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('videos')
        .getPublicUrl(filePath);

      onSettingsChange({ 
        ...settings, 
        motion_video: publicUrl,
        motion_video_duration: videoDuration 
      });
      toast({ title: "Video uploaded", description: `${videoDuration}s motion video set` });
    } catch (error) {
      console.error('Video upload error:', error);
      toast({ title: "Upload failed", description: "Failed to upload video", variant: "destructive" });
    } finally {
      setIsUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleRemoveMotionVideo = () => {
    onSettingsChange({ ...settings, motion_video: '', motion_video_duration: undefined });
  };

  const updateSetting = (key: keyof VideoSettings, value: any) => {
    let newSettings = { ...settings, [key]: value };

    // When model changes, adjust settings to match model capabilities
    if (key === "model") {
      const newModel = VIDEO_MODELS[value];
      if (newModel) {
        // Reset to model's first available options
        newSettings.duration = newModel.durations[0];
        newSettings.quality = newModel.qualities[0];
        newSettings.aspect_ratio = newModel.aspectRatios[0];
        // Reset style for models that don't support it
        if (!newModel.supports.styles) {
          newSettings.style = "None";
        }
        // Reset audio if model doesn't support it
        if (!newModel.supportsAudio && !newModel.optionalAudio) {
          newSettings.enable_audio = false;
        }
      }
    }

    // Auto-correct incompatible combinations for PixVerse v5
    if (settings.model === 'pixverse-v5') {
      // 1080p + smooth is not allowed
      if (key === "quality" && value === "1080p") {
        if (newSettings.motion_mode === "smooth") newSettings.motion_mode = "normal";
      }
      // Smooth motion only for 5s
      if (key === "motion_mode" && value === "smooth") {
        if (newSettings.duration === 8) newSettings.duration = 5;
        if (newSettings.quality === "1080p") newSettings.quality = "720p";
      }
    }

    // Hailuo 02: 1080p only available for 6s duration
    if (settings.model === 'hailuo-02') {
      if (key === "quality" && value === "1080p") {
        if (newSettings.duration === 10) newSettings.duration = 6;
      }
      if (key === "duration" && value === 10) {
        if (newSettings.quality === "1080p") newSettings.quality = "768p";
      }
    }

    onSettingsChange(newSettings);
  };

  // Model-specific constraints for PixVerse v5
  const is1080pDisabled = settings.model === 'pixverse-v5' && settings.motion_mode === "smooth";
  const is8sDisabled = settings.model === 'pixverse-v5' && settings.motion_mode === "smooth";
  // Hailuo 02: 1080p only for 6s
  const isHailuo1080pDisabled = settings.model === 'hailuo-02' && settings.duration === 10;

  // Dynamic options based on current model
  const qualityOptions = currentModel.qualities.map(q => ({
    value: q,
    label: q === "1080p" ? "1080p (HD)" : q === "720p" ? "720p (Standard)" : q === "768p" ? "768p (Medium)" : q === "512p" ? "512p (Fast)" : `${q} (Fast)`,
    disabled: (q === "1080p" && is1080pDisabled) || (q === "1080p" && isHailuo1080pDisabled)
  }));

  const durationOptions = currentModel.durations.map(d => ({
    value: d,
    label: `${d} seconds`,
    disabled: d === 8 && is8sDisabled
  }));

  const aspectOptions = currentModel.aspectRatios.map(ar => ({
    value: ar,
    label: ar === "16:9" ? "16:9 (Landscape)" : ar === "9:16" ? "9:16 (Portrait)" : "1:1 (Square)"
  }));

  const styleOptions = [
    { value: "None", label: "Realistic" },
    { value: "anime", label: "Anime" },
    { value: "3d_animation", label: "3D Animation" },
    { value: "clay", label: "Clay" },
    { value: "comic", label: "Comic" },
  ];

  // Group models by tier, sorted by cheapest first within each tier
  // Order: wan-super (500), seedance-1-pro-fast, seedance-1.5-pro, hailuo-02
  const budgetOrder = ['wan-super', 'seedance-1-pro-fast', 'hailuo-02', 'seedance-1.5-pro'];
  const budgetModels = Object.entries(VIDEO_MODELS)
    .filter(([_, m]) => m.tier === 'budget')
    .sort((a, b) => budgetOrder.indexOf(a[0]) - budgetOrder.indexOf(b[0]));
  
  // Order: pixverse-v5 (cheaper at 5s), kling-v2.6
  const mediumOrder = ['pixverse-v5', 'kling-v2.6'];
  const mediumModels = Object.entries(VIDEO_MODELS)
    .filter(([_, m]) => m.tier === 'standard')
    .sort((a, b) => mediumOrder.indexOf(a[0]) - mediumOrder.indexOf(b[0]));
  
  // Order: sora-2, veo-3.1-fast, veo-3.1, sora-2-pro
  const highOrder = ['sora-2', 'veo-3.1-fast', 'veo-3.1', 'sora-2-pro'];
  const highModels = Object.entries(VIDEO_MODELS)
    .filter(([_, m]) => m.tier === 'premium')
    .sort((a, b) => highOrder.indexOf(a[0]) - highOrder.indexOf(b[0]));
  
  const animateModels = Object.entries(VIDEO_MODELS).filter(([_, m]) => m.tier === 'image-to-video');

  const buttonClass = "rounded-full bg-secondary hover:bg-secondary/80 text-secondary-foreground px-2.5 md:px-4 py-1.5 md:py-2 h-8 md:h-9 text-xs md:text-sm font-medium";

  const getQualityLabel = () => {
    const opt = qualityOptions.find(o => o.value === settings.quality);
    return opt?.label.split(" ")[0] || settings.quality;
  };

  const getDurationLabel = () => {
    return `${settings.duration}s`;
  };

  const getStyleLabel = () => {
    const opt = styleOptions.find(o => o.value === settings.style);
    return opt?.label || "Realistic";
  };

  const getModelLabel = () => {
    return currentModel.displayName;
  };

  return (
    <div className="flex flex-wrap items-end justify-center gap-2 md:gap-4">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      <input
        ref={lastFrameInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleLastFrameSelect}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={handleVideoSelect}
      />

      {/* Start Image / Character Image for Kling Motion Control */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">
          {settings.model === 'kling-motion-control' ? 'Character' : 'Start Image'}
          {settings.model === 'kling-motion-control' && <span className="text-destructive ml-0.5">*</span>}
        </span>
        {settings.start_image ? (
          <div className="relative group">
            <img 
              src={settings.start_image} 
              alt="Start frame" 
              className="w-9 h-9 object-cover rounded-lg border border-border"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="rounded-full h-9 px-3 border-dashed"
          >
            <Plus className="h-4 w-4 mr-1" />
            {isUploading ? "..." : "Add"}
          </Button>
        )}
      </div>

      {/* Last Frame Image - Only for models that support it and when start image is set */}
      {currentModel.supports.lastFrameImage && settings.start_image && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Last Frame</span>
          {settings.last_frame_image ? (
            <div className="relative group">
              <img 
                src={settings.last_frame_image} 
                alt="Last frame" 
                className="w-9 h-9 object-cover rounded-lg border border-border"
              />
              <button
                onClick={handleRemoveLastFrame}
                className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => lastFrameInputRef.current?.click()}
              disabled={isUploadingLastFrame}
              className="rounded-full h-9 px-3 border-dashed"
            >
              <Plus className="h-4 w-4 mr-1" />
              {isUploadingLastFrame ? "..." : "Add"}
            </Button>
          )}
        </div>
      )}

      {/* Motion Video - Required for Kling Motion Control */}
      {settings.model === 'kling-motion-control' && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            Motion Video
            <span className="text-destructive">*</span>
            <span className="text-[10px]">(max 30s)</span>
          </span>
          
          {/* Show preset gallery when no video OR when a preset is selected */}
          {(!settings.motion_video || isMotionPreset(settings.motion_video)) ? (
            <div className="flex flex-col items-center gap-2">
              <MotionPresetGallery
                selectedPreset={settings.motion_video || null}
                onSelect={(videoUrl, duration) => {
                  onSettingsChange({
                    ...settings,
                    motion_video: videoUrl,
                    motion_video_duration: duration
                  });
                }}
                onClear={handleRemoveMotionVideo}
              />
              {/* Show upload button only when no preset is selected */}
              {!settings.motion_video && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={isUploadingVideo}
                  className="rounded-full h-7 px-2 border-dashed text-xs"
                >
                  <Film className="h-3 w-3 mr-1" />
                  {isUploadingVideo ? "..." : "Or upload"}
                </Button>
              )}
            </div>
          ) : (
            /* Custom uploaded video - show green indicator */
            <div className="relative group">
              <div className="w-9 h-9 rounded-lg border-2 border-green-500 bg-green-500/10 flex items-center justify-center">
                <Film className="h-4 w-4 text-green-500" />
              </div>
              <span className="text-[10px] text-muted-foreground mt-0.5">
                {settings.motion_video_duration}s
              </span>
              <button
                onClick={handleRemoveMotionVideo}
                className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mode Selector - For Kling Motion Control (replaces Quality) */}
      {settings.model === 'kling-motion-control' && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Mode</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className={buttonClass}>
                {settings.quality === 'pro' ? 'Pro' : 'Standard'}
                <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border">
              <DropdownMenuItem onClick={() => updateSetting("quality", "standard")}>
                Standard
                {settings.quality === 'standard' && <Check className="ml-2 h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateSetting("quality", "pro")}>
                Pro
                {settings.quality === 'pro' && <Check className="ml-2 h-4 w-4" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Model Selector */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">Model</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className={buttonClass}>
              <span className="flex items-center gap-1.5">
                {getModelLabel()}
                {currentModel.supportsAudio && <Volume2 className="h-3 w-3 text-primary" />}
              </span>
              <ChevronDown className="ml-1.5 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover border-border min-w-[200px] p-0 relative">
            <ScrollArea className="h-[320px]">
              <div className="p-1">
                {/* Budget Models */}
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Budget</div>
                {budgetModels.map(([key, model]) => (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => updateSetting("model", key)}
                    className="flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      {model.displayName}
                      <ImageIcon className="h-3 w-3 text-muted-foreground" />
                      {model.supportsAudio && <Volume2 className="h-3 w-3 text-primary" />}
                    </span>
                    {settings.model === key && <Check className="h-4 w-4 ml-2" />}
                  </DropdownMenuItem>
                ))}
                
                <DropdownMenuSeparator />
                
                {/* Medium Models */}
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Medium</div>
                {mediumModels.map(([key, model]) => (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => updateSetting("model", key)}
                    className="flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      {model.displayName}
                      <ImageIcon className="h-3 w-3 text-muted-foreground" />
                      {(model.supportsAudio || model.optionalAudio) && <Volume2 className="h-3 w-3 text-primary" />}
                    </span>
                    {settings.model === key && <Check className="h-4 w-4 ml-2" />}
                  </DropdownMenuItem>
                ))}
                
                <DropdownMenuSeparator />
                
                {/* High Models */}
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium">High</div>
                {highModels.map(([key, model]) => (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => updateSetting("model", key)}
                    className="flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      {model.displayName}
                      <ImageIcon className="h-3 w-3 text-muted-foreground" />
                      {model.supportsAudio && <Volume2 className="h-3 w-3 text-primary" />}
                    </span>
                    {settings.model === key && <Check className="h-4 w-4 ml-2" />}
                  </DropdownMenuItem>
                ))}
                
                {animateModels.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    
                    {/* Animate Your Image */}
                    <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Animate Your Image</div>
                    {animateModels.map(([key, model]) => (
                      <DropdownMenuItem
                        key={key}
                        onClick={() => updateSetting("model", key)}
                        className="flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          {model.displayName}
                          <ImageIcon className="h-3 w-3 text-muted-foreground" />
                          {model.supportsAudio && <Volume2 className="h-3 w-3 text-primary" />}
                        </span>
                        {settings.model === key && <Check className="h-4 w-4 ml-2" />}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
            {/* Fade gradient indicator at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-popover to-transparent pointer-events-none rounded-b-md" />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Quality - Hide for models with no quality options (Sora 2) or Kling Motion Control (has Mode selector) */}
      {qualityOptions.length > 0 && settings.model !== 'kling-motion-control' && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Quality</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className={buttonClass}>
                {getQualityLabel()} <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border">
              {qualityOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => !opt.disabled && updateSetting("quality", opt.value)}
                  disabled={opt.disabled}
                  className="flex items-center justify-between"
                >
                  {opt.label}
                  {settings.quality === opt.value && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Duration - Hide for models where duration comes from input video */}
      {durationOptions.length > 0 && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Duration</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className={buttonClass}>
                {getDurationLabel()} <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border">
              {durationOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => !opt.disabled && updateSetting("duration", opt.value)}
                  disabled={opt.disabled}
                  className="flex items-center justify-between"
                >
                  {opt.label}
                  {settings.duration === opt.value && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Aspect Ratio - Hide for models where aspect ratio comes from image */}
      {aspectOptions.length > 0 && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Aspect Ratio</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className={buttonClass}>
                {settings.aspect_ratio} <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border">
              {aspectOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => updateSetting("aspect_ratio", opt.value)}
                  className="flex items-center justify-between"
                >
                  {opt.label}
                  {settings.aspect_ratio === opt.value && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Style - only for models that support it */}
      {currentModel.supports.styles && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Style</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className={buttonClass}>
                {getStyleLabel()} <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border">
              {styleOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => updateSetting("style", opt.value)}
                  className="flex items-center justify-between"
                >
                  {opt.label}
                  {settings.style === opt.value && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}


      {/* Audio Toggle - Only for models with optional audio */}
      {currentModel.optionalAudio && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Audio</span>
          <Button
            variant={settings.enable_audio ? "default" : "secondary"}
            className={`rounded-full h-9 px-4 ${settings.enable_audio ? 'bg-primary text-primary-foreground' : ''}`}
            onClick={() => updateSetting('enable_audio', !settings.enable_audio)}
          >
            <Volume2 className="h-4 w-4 mr-1.5" />
            {settings.enable_audio ? 'On' : 'Off'}
          </Button>
        </div>
      )}

      {/* Reset Button */}
      {hasChangedSettings && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onUseDefaults}
          className="rounded-full h-9 px-3 text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}